import { Component, type ErrorInfo, type ReactNode } from 'react';
import { report } from './errorReporter';
import { shouldReloadAfterChunkError } from './lazyWithReload';
import { hideSplash } from './splash';

const AUTO_RELOAD_MS = 10_000;

interface Props {
  children: ReactNode;
  /** Injected in tests. */
  reload?: () => void;
  canAutoReload?: () => boolean;
}

interface State {
  failed: boolean;
  willReload: boolean;
}

function defaultCanAutoReload(): boolean {
  try {
    return shouldReloadAfterChunkError(Date.now(), window.sessionStorage);
  } catch {
    return false;
  }
}

/**
 * Without this, any render error unmounts the whole tree and a wall display shows only the page
 * background (seen on the iPad as a plain blue screen). Reports the error, then restarts the app
 * by itself; if restarts keep failing, it waits for a tap instead of looping.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, willReload: false };
  private timer: ReturnType<typeof setTimeout> | undefined;

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    hideSplash(true);
    report('error', `render: ${error.message}`, `${error.stack || ''}\n--- component stack ---${info.componentStack || ''}`);
    const can = (this.props.canAutoReload || defaultCanAutoReload)();
    this.setState({ willReload: can });
    if (can) this.timer = setTimeout(() => this.reload(), AUTO_RELOAD_MS);
  }

  componentWillUnmount(): void {
    if (this.timer) clearTimeout(this.timer);
  }

  private reload = () => (this.props.reload || (() => window.location.reload()))();

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center px-8 text-center"
        style={{ background: 'linear-gradient(155deg,#16408F 0%,#2F6CC0 40%,#4C93D3 68%,#2A8C8C 100%)', color: '#fff' }}
      >
        <div className="flex h-[52px] w-[72px] items-center justify-center rounded-[14px] bg-white font-serif text-[22px]" style={{ color: '#163E80' }}>
          OBV
        </div>
        <p className="mt-6 text-[20px]">Something went wrong.</p>
        <p className="mt-2 max-w-[460px] text-[15px] opacity-90">
          {this.state.willReload ? 'Restarting the display in a few seconds…' : 'Tap below to try again.'}
        </p>
        <button type="button" onClick={this.reload} className="tap mt-6 h-[44px] rounded-[12px] bg-white px-6 text-[15px] font-medium" style={{ color: '#163E80' }}>
          Restart now
        </button>
      </div>
    );
  }
}
